//src/services/appointmentApi.js
import API from "./interceptor";

// ******************* Create new appointment *******************
export const createAppointment = async (appointmentData) => {
  try {
    const response = await API.post(`/api/appointments`, appointmentData);
    return response.data;
  } catch (error) {
    throw error?.response?.data?.message || "Failed to create appointment";
  }
};

// ******************* Get all appointments with filtering and pagination *******************
export const getAllAppointments = async (queryParams = {}) => {
  try {
    const response = await API.get(`/api/appointments`, {
      params: queryParams,
    });
    return response.data;
  } catch (error) {
    throw error?.response?.data?.message || "Failed to fetch appointments";
  }
};

// ******************* Get single appointment by ID *******************
export const getAppointmentById = async (appointmentId) => {
  try {
    const response = await API.get(`/api/appointments/${appointmentId}`);
    return response.data;
  } catch (error) {
    throw error?.response?.data?.message || "Failed to fetch appointment";
  }
};

// ******************* Update appointment information *******************
export const updateAppointment = async (appointmentId, updateData) => {
  try {
    const response = await API.put(
      `/api/appointments/${appointmentId}`,
      updateData
    );
    return response.data;
  } catch (error) {
    throw error?.response?.data?.message || "Failed to update appointment";
  }
};

// ******************* Delete appointment *******************
export const deleteAppointment = async (appointmentId) => {
  try {
    const response = await API.delete(`/api/appointments/${appointmentId}`);
    return response.data;
  } catch (error) {
    throw error?.response?.data?.message || "Failed to delete appointment";
  }
};

// ******************* Get today's appointments *******************
export const getTodaysAppointments = async () => {
  try {
    const response = await API.get(`/api/appointments/today`);
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to fetch today's appointments"
    );
  }
};

// ******************* Get appointments by status *******************
export const getAppointmentsByStatus = async (status) => {
  try {
    const response = await API.get(`/api/appointments/status/${status}`);
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to fetch appointments by status"
    );
  }
};

// ******************* Get appointments by pet ID *******************
export const getAppointmentsByPet = async (petId) => {
  try {
    const response = await API.get(`/api/appointments/pet/${petId}`);
    return response.data;
  } catch (error) {
    throw error?.response?.data?.message || "Failed to fetch pet appointments";
  }
};

// ******************* Get appointments by owner ID *******************
export const getAppointmentsByOwner = async (ownerId) => {
  try {
    const response = await API.get(`/api/appointments/owner/${ownerId}`);
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to fetch owner appointments"
    );
  }
};

// ******************* Additional utility functions *******************

// Get appointments by type
export const getAppointmentsByType = async (type) => {
  try {
    const response = await API.get(`/api/appointments`, {
      params: { type },
    });
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to fetch appointments by type"
    );
  }
};

// Get appointments by doctor
export const getAppointmentsByDoctor = async (doctor) => {
  try {
    const response = await API.get(`/api/appointments`, {
      params: { doctor },
    });
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to fetch appointments by doctor"
    );
  }
};

// Get appointments by date
export const getAppointmentsByDate = async (date) => {
  try {
    const response = await API.get(`/api/appointments`, {
      params: { date },
    });
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to fetch appointments by date"
    );
  }
};

// Get appointments by date range
export const getAppointmentsByDateRange = async (startDate, endDate) => {
  try {
    const response = await API.get(`/api/appointments`, {
      params: {
        startDate,
        endDate,
      },
    });
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message ||
      "Failed to fetch appointments by date range"
    );
  }
};

// Search appointments with multiple filters
export const searchAppointments = async (filters) => {
  try {
    const response = await API.get(`/api/appointments`, { params: filters });
    return response.data;
  } catch (error) {
    throw error?.response?.data?.message || "Failed to search appointments";
  }
};

// Get confirmed appointments
export const getConfirmedAppointments = async () => {
  try {
    const response = await API.get(`/api/appointments/status/confirmed`);
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to fetch confirmed appointments"
    );
  }
};

// Get pending appointments
export const getPendingAppointments = async () => {
  try {
    const response = await API.get(`/api/appointments/status/pending`);
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to fetch pending appointments"
    );
  }
};

// Get cancelled appointments
export const getCancelledAppointments = async () => {
  try {
    const response = await API.get(`/api/appointments/status/cancelled`);
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to fetch cancelled appointments"
    );
  }
};

// Get completed appointments
export const getCompletedAppointments = async () => {
  try {
    const response = await API.get(`/api/appointments/status/completed`);
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to fetch completed appointments"
    );
  }
};

// Cancel appointment (update status to cancelled)
export const cancelAppointment = async (appointmentId) => {
  try {
    const response = await API.put(`/api/appointments/${appointmentId}`, {
      status: "cancelled",
    });
    return response.data;
  } catch (error) {
    throw error?.response?.data?.message || "Failed to cancel appointment";
  }
};

// Confirm appointment (update status to confirmed)
export const confirmAppointment = async (appointmentId) => {
  try {
    const response = await API.put(`/api/appointments/${appointmentId}`, {
      status: "confirmed",
    });
    return response.data;
  } catch (error) {
    throw error?.response?.data?.message || "Failed to confirm appointment";
  }
};

// Complete appointment (update status to completed)
export const completeAppointment = async (appointmentId) => {
  try {
    const response = await API.put(`/api/appointments/${appointmentId}`, {
      status: "completed",
    });
    return response.data;
  } catch (error) {
    throw error?.response?.data?.message || "Failed to complete appointment";
  }
};

// Reschedule appointment
export const rescheduleAppointment = async (
  appointmentId,
  newDate,
  newTime
) => {
  try {
    const response = await API.put(`/api/appointments/${appointmentId}`, {
      date: newDate,
      time: newTime,
    });
    return response.data;
  } catch (error) {
    throw error?.response?.data?.message || "Failed to reschedule appointment";
  }
};
